/**
 * Thin HTTP client for item requests — no browser Supabase writes.
 * Domain mapping / persist live in `@/lib/item-requests`.
 */
import { supabase } from "./supabase";
import { ItemRequest, ItemCategory } from "./types";
import type { Database } from "./database.types";
import { mapSupabaseItemRequestToItemRequest } from "./item-requests";

export { mapSupabaseItemRequestToItemRequest } from "./item-requests";

type SupabaseItemRequest = Database["public"]["Tables"]["item_requests"]["Row"];

async function getBearerToken(): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) {
    throw new Error("Please log in to continue");
  }
  return token;
}

async function parseRequestPayload(response: Response): Promise<{
  request?: SupabaseItemRequest;
  requests?: SupabaseItemRequest[];
  error?: string;
}> {
  return (await response.json().catch(() => ({}))) as {
    request?: SupabaseItemRequest;
    requests?: SupabaseItemRequest[];
    error?: string;
  };
}

function mapRows(rows: SupabaseItemRequest[] | undefined): ItemRequest[] {
  if (!rows?.length) return [];
  return rows.map(mapSupabaseItemRequestToItemRequest);
}

/** Public list of active approved requests (optional limit). */
export const getItemRequests = async (
  limit?: number,
): Promise<ItemRequest[]> => {
  try {
    const qs = limit ? `?limit=${limit}` : "";
    const response = await fetch(`/api/item-requests${qs}`);
    const payload = await parseRequestPayload(response);
    if (!response.ok) {
      throw new Error(payload.error || "Failed to fetch item requests");
    }
    return mapRows(payload.requests);
  } catch (error) {
    console.error("Error in getItemRequests:", error);
    return [];
  }
};

export const getRecentItemRequests = async (
  limit: number = 12,
): Promise<ItemRequest[]> => {
  return getItemRequests(limit);
};

/** Current user's requests (auth). */
export const getUserItemRequests = async (
  _userId?: string,
): Promise<ItemRequest[]> => {
  try {
    const token = await getBearerToken();
    const response = await fetch("/api/item-requests?mine=1", {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payload = await parseRequestPayload(response);
    if (!response.ok) {
      throw new Error(payload.error || "Failed to fetch your requests");
    }
    return mapRows(payload.requests);
  } catch (error) {
    console.error("Error in getUserItemRequests:", error);
    return [];
  }
};

export const createItemRequest = async (requestData: {
  category: ItemCategory;
  subcategory: string;
  categoryName: string;
  subcategoryName: string;
  userLocation: string;
  maxPrice?: number;
  urgency: "low" | "medium" | "high";
  notes?: string;
  budgetSoftWarnAcknowledged?: boolean;
  preferenceAttrs?: Record<string, string | number | boolean>;
}): Promise<ItemRequest> => {
  const token = await getBearerToken();

  const response = await fetch("/api/item-requests", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      category: requestData.category,
      subcategory: requestData.subcategory,
      categoryName: requestData.categoryName,
      subcategoryName: requestData.subcategoryName,
      userLocation: requestData.userLocation,
      maxPrice: requestData.maxPrice,
      urgency: requestData.urgency,
      notes: requestData.notes,
      budgetSoftWarnAcknowledged: requestData.budgetSoftWarnAcknowledged,
      preferenceAttrs: requestData.preferenceAttrs,
    }),
  });

  const payload = await parseRequestPayload(response);

  if (!response.ok) {
    throw new Error(payload.error || "Failed to create item request");
  }
  if (!payload.request) {
    throw new Error("Failed to create item request");
  }

  return mapSupabaseItemRequestToItemRequest(payload.request);
};

/** Owner edit: notes / maxPrice / urgency (validated server-side). */
export const updateItemRequest = async (
  requestId: string,
  fields: {
    notes?: string;
    maxPrice?: number | null;
    urgency: "low" | "medium" | "high";
  },
): Promise<ItemRequest> => {
  const token = await getBearerToken();

  const response = await fetch(`/api/item-requests/${requestId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(fields),
  });

  const payload = await parseRequestPayload(response);
  if (!response.ok) {
    throw new Error(payload.error || "Failed to update item request");
  }
  if (!payload.request) {
    throw new Error("Failed to update item request");
  }
  return mapSupabaseItemRequestToItemRequest(payload.request);
};

export const updateItemRequestStatus = async (
  requestId: string,
  status: "active" | "fulfilled" | "expired",
): Promise<ItemRequest> => {
  const token = await getBearerToken();

  const response = await fetch(`/api/item-requests/${requestId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ status }),
  });

  const payload = await parseRequestPayload(response);
  if (!response.ok) {
    throw new Error(payload.error || "Failed to update item request status");
  }
  if (!payload.request) {
    throw new Error("Failed to update item request status");
  }
  return mapSupabaseItemRequestToItemRequest(payload.request);
};

export const deleteItemRequest = async (requestId: string): Promise<void> => {
  const token = await getBearerToken();

  const response = await fetch(`/api/item-requests/${requestId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });

  const payload = await parseRequestPayload(response);
  if (!response.ok) {
    throw new Error(payload.error || "Failed to delete item request");
  }
};

export type ItemRequestMatchDto = {
  band: "strong" | "soft" | "exploratory" | "over_budget";
  score: number;
  breakdown: {
    categoryMatch: boolean;
    subcategoryMatch: boolean;
    geoTier: "neighborhood" | "lga" | "state" | "none";
    inBudget: boolean;
    overBudget: boolean;
    budgetOpen: boolean;
    preferenceFit: "unset" | "full" | "partial" | "none";
    preferenceKeysCompared: number;
    preferenceKeysMatched: number;
  };
  listing: {
    id: string;
    title: string;
    price: number;
    category: string;
    subcategory: string;
    location: string;
    imageUrl: string | null;
  };
};

export type ItemRequestMatchesResponse = {
  eligible: boolean;
  matches: {
    strong: ItemRequestMatchDto[];
    soft: ItemRequestMatchDto[];
    exploratory: ItemRequestMatchDto[];
    overBudget: ItemRequestMatchDto[];
    ordered: ItemRequestMatchDto[];
  };
};

/** Ordered matches for an owned request (Phase 2 pull). */
export const getItemRequestMatches = async (
  requestId: string,
): Promise<ItemRequestMatchesResponse> => {
  const token = await getBearerToken();
  const response = await fetch(`/api/item-requests/${requestId}/matches`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const payload = (await response
    .json()
    .catch(() => ({}))) as ItemRequestMatchesResponse & { error?: string };
  if (!response.ok) {
    throw new Error(payload.error || "Failed to load matches");
  }
  return payload;
};
